import React from 'react'
import { Typography } from '@mui/material'

export const isObject = item => {
  return item && typeof item === 'object'
}

export const isArray = item => {
  return item && Array.isArray(item)
}

export const isFunctionText = optionText => {
  return optionText && typeof optionText === 'function'
}

export const isArrayOfObjects = items => {
  return isArray(items) && items.some(item => item && isObject(item))
}

export const isValueAllowed = (value, restrictedValues) => {
  if (!restrictedValues) return true
  return restrictedValues?.test(value) || value === ''
}

export const createMap = (list, property) => {
  const map = {}

  for (const item of list) {
    const key = item[property]

    if (typeof key === 'string' || typeof key === 'number') {
      map[key] = item
    } else {
      throw new Error(`Invalid key type: ${typeof key}`)
    }
  }

  return map
}

export const getOptionsMap = (options, optionValue) => {
  let map = {}
  if (!isArrayOfObjects(options)) return map

  try {
    map = createMap(options, optionValue)
  } catch {
    map = {}
  }
  return map
}

export const compareValues = (option, value, optionValue) => {
  const isOptionObject = isObject(option)
  const isValueObject = isObject(value)

  if (isOptionObject && isValueObject)
    return option[optionValue] === value[optionValue]
  if (isOptionObject && !isValueObject) return option[optionValue] === value

  return option === value
}

export const getValueValidity = (value, options, optionValue) => {
  const isValueObject = isObject(value)
  const isValueArray = isArray(value)

  if (isValueArray)
    return value.some(val =>
      options.some(option => compareValues(option, val, optionValue))
    )

  return isValueObject
    ? options.some(option =>
        compareValues(option[optionValue], value[optionValue], optionValue)
      )
    : options.some(option => compareValues(option, value, optionValue))
}

export const getOptionLabel = (option, optionText) => {
  if (!option) return ''

  const isOptionObject = isObject(option)
  const isFunction = isFunctionText(optionText)
  if (isOptionObject)
    return isFunction ? optionText(option) : option[optionText]

  return option
}

export const getSelectLabel = (selected, options, optionText, optionValue) => {
  if (isArray(selected))
    return selected
      .map(val => {
        const matchedOption = options.find(opt =>
          compareValues(opt, val, optionValue)
        )
        return matchedOption ? getOptionLabel(matchedOption, optionText) : val
      })
      .join(', ')

  const matchedOption = options.find(opt =>
    compareValues(opt, selected, optionValue)
  )
  return matchedOption ? getOptionLabel(matchedOption, optionText) : selected
}

export const getSelectValue = (
  value,
  optionValue,
  isValueValid,
  isValueArray
) => {
  if (isValueValid) {
    if (isValueArray)
      return value.map(val => (isObject(val) ? val[optionValue] : val))
    else if (isObject(value)) return value[optionValue]
    return value
  }

  return isValueArray ? [] : ''
}

export const getSelectChangePayload = (
  newValue,
  isValueArray,
  optionsMap,
  returnObject
) => {
  let payload = newValue
  if (returnObject) {
    payload = isValueArray
      ? newValue.map(val => optionsMap[val])
      : optionsMap[newValue]
  }
  return payload
}

export const getAutocompleteValue = (
  value,
  options,
  optionsMap,
  isValueValid,
  isValueArray,
  returnObject
) => {
  const isOptionsArrayOfObjects = isArrayOfObjects(options)

  if (isValueValid) {
    if (isOptionsArrayOfObjects && !returnObject)
      return isValueArray
        ? value.map(val => optionsMap[val])
        : optionsMap[value]

    return value
  }
  return isValueArray ? [] : null
}

export const isOptionAndValueEqual = (option, value, optionValue) => {
  if (!option || !value) return false

  const isOptionObject = isObject(option)
  const isValueObject = isObject(value)
  if (isOptionObject && !isValueObject) return option[optionValue] === value
  if (isOptionObject && isValueObject)
    return option[optionValue] === value[optionValue]

  return option === value
}

export const getAutocompleteChangePayload = (
  newValue,
  optionValue,
  isValueArray,
  returnObject
) => {
  if (!newValue) return isValueArray ? [] : ''

  const isNewValueObject = isObject(newValue)
  const isNewValueArrayOfObjects = isArrayOfObjects(newValue)

  if (!returnObject) {
    if (isValueArray && isNewValueArrayOfObjects)
      return newValue.map(val => val[optionValue])
    else if (!isValueArray && isNewValueObject) return newValue[optionValue]
  }

  return newValue
}

export const isAutoCompleteInputSame = (value, optionText, inputValue) => {
  const isFunction = isFunctionText(optionText)
  return value
    ? isObject(value)
      ? isFunction
        ? optionText(value).trim().toLowerCase() ===
          inputValue.trim().toLowerCase()
        : value[optionText] === inputValue
      : value.trim().toLowerCase() === inputValue.trim().toLowerCase()
    : false
}

export const sortOptions = (options, optionText, groupByProp) => {
  const isOptionsArrayOfObjects = isArrayOfObjects(options)
  const isFunction = isFunctionText(optionText)

  if (isOptionsArrayOfObjects) {
    if (groupByProp) {
      const grouped = options.reduce((acc, item) => {
        const groupKey = groupByProp(item)
        if (!acc[groupKey]) acc[groupKey] = []
        acc[groupKey].push(item)
        return acc
      }, {})

      Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => {
          const aText = isFunction ? optionText(a) : a[optionText]
          const bText = isFunction ? optionText(b) : b[optionText]
          return (aText || '').localeCompare(bText || '')
        })
      })

      return Object.values(grouped).flat()
    } else {
      return options.sort((a, b) => {
        const aText = isFunction ? optionText(a) : a[optionText]
        const bText = isFunction ? optionText(b) : b[optionText]
        return (aText || '').localeCompare(bText || '')
      })
    }
  } else {
    return options.sort((a, b) => {
      if (typeof a === 'string' && typeof b === 'string') {
        return a.localeCompare(b)
      }
      return a - b
    })
  }
}

export const filterOptions = (
  autocompleteValue,
  options,
  inputValue,
  optionText
) => {
  if (!inputValue) return options
  if (
    getOptionLabel(autocompleteValue, optionText)?.toLowerCase() ===
    inputValue.toLowerCase()
  )
    return options

  const searchTerm = inputValue.toLowerCase()
  return options.filter(option => {
    const label = getOptionLabel(option, optionText)
    return label && label.toLowerCase().includes(searchTerm)
  })
}

export const formatMaskedPhoneNumber = phoneNumber => {
  if (!phoneNumber) return ''
  const cleanedNumber = phoneNumber.replace(/[^\d]/g, '')
  return `+1${cleanedNumber}`
}

export const getLabel = (label = '', required) => {
  return (
    <span>
      {label}
      {required && (
        <Typography
          component='span'
          sx={{ color: 'error.main', fontSize: 'inherit' }}
        >
          *
        </Typography>
      )}
    </span>
  )
}

export const getComputedWidth = (width, fullWidth) => {
  return width ? width : fullWidth ? '100%' : '240px'
}

export const minutesToLabel = minutes => {
  const total = Math.max(0, Math.round(minutes))
  const hours24 = Math.floor(total / 60) % 24
  const mins = total % 60
  const isPM = hours24 >= 12
  const suffix = isPM ? 'PM' : 'AM'

  const hour12 = hours24 % 12 === 0 ? 12 : hours24 % 12
  const hoursDisplay = hours24 === 0 ? '00' : String(hour12).padStart(2, '0')
  const minsDisplay = String(mins).padStart(2, '0')
  return `${hoursDisplay}:${minsDisplay} ${suffix}`
}

export const formatValueForDisplay = (value, formatter) => {
  return Array.isArray(value)
    ? `${formatter(value[0])} - ${formatter(value[1])}`
    : formatter(value)
}

export const labelToMinutes = label => {
  if (typeof label !== 'string') return 0
  const match = label.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)/i)
  if (!match) return 0
  let hours = parseInt(match[1], 10)
  const minutes = parseInt(match[2], 10)
  const period = match[3].toUpperCase()

  if (hours === 0 && period === 'AM') {
    return minutes
  }
  if (period === 'AM') {
    hours = hours % 12
  } else {
    hours = (hours % 12) + 12
  }
  return hours * 60 + minutes
}
